import React, { useContext, useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { useSetup } from "../hooks/useSetup";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import QrcodeModal from "../../../components/QrcodeModal";
import { AuthContext } from "../../../context/Auth/AuthContext";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  connectionCard: {
    width: "100%",
    maxWidth: 820,
    boxSizing: "border-box",
    padding: 22,
    display: "grid",
    gridTemplateColumns: "64px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 18,
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "52px minmax(0, 1fr)",
      padding: 18
    }
  },

  icon: {
    width: 58,
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#25D366,#128C4A)",
    boxShadow: "0 12px 25px rgba(37,211,102,.22)",

    [theme.breakpoints.down("sm")]: {
      width: 48,
      height: 48,
      borderRadius: 14
    }
  },

  iconSvg: {
    fontSize: 30,

    [theme.breakpoints.down("sm")]: {
      fontSize: 25
    }
  },

  information: {
    minWidth: 0
  },

  title: {
    color: theme.palette.text.primary,
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.3
  },

  text: {
    maxWidth: 520,
    marginTop: 5,
    color: theme.palette.text.secondary,
    fontSize: 12.5,
    lineHeight: 1.55
  },

  actionArea: {
    display: "flex",
    justifyContent: "flex-end",

    [theme.breakpoints.down("sm")]: {
      gridColumn: "1 / -1",
      width: "100%"
    }
  },

  connectButton: {
    minHeight: 42,
    padding: "8px 16px",
    borderRadius: 10,
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "none",
    background: "#16A34A",
    boxShadow: "none",

    "&:hover": {
      background: "#15803D",
      boxShadow: "none"
    },

    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },

  status: {
    minWidth: 176,
    boxSizing: "border-box",
    padding: "11px 13px",
    display: "flex",
    alignItems: "center",
    gap: 9,
    borderRadius: 11,
    border: "1px solid rgba(22,163,74,.2)",
    background: "rgba(22,163,74,.08)",

    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },

  statusIcon: {
    flexShrink: 0,
    color: "#16A34A",
    fontSize: 22
  },

  statusTitle: {
    color: theme.palette.text.primary,
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.25
  },

  statusText: {
    marginTop: 2,
    color: theme.palette.text.secondary,
    fontSize: 10.5,
    lineHeight: 1.3
  },

  note: {
    maxWidth: 820,
    marginTop: 18,
    padding: 15,
    boxSizing: "border-box",
    borderRadius: 13,
    border: "1px solid rgba(21,94,239,.16)",
    background:
      theme.palette.type === "dark"
        ? "rgba(21,94,239,.08)"
        : "rgba(21,94,239,.045)"
  },

  noteTitle: {
    color: theme.palette.text.primary,
    fontSize: 12,
    fontWeight: 800
  },

  noteText: {
    marginTop: 4,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.5
  }
}));

export default function WhatsappStep() {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const {
    whatsapp,
    updateWhatsapp,
    nextStep,
    previousStep
  } = useSetup();

  const handleConnect = async () => {
    setConnecting(true);

    try {
      const { data } = await api.post("/whatsapp/onboarding");

      updateWhatsapp({
        connected: data?.status === "CONNECTED",
        connectionId: data?.id || null
      });

      if (data?.status !== "CONNECTED") {
        setQrModalOpen(true);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    if (!socket || !user?.companyId || !whatsapp?.connectionId) {
      return undefined;
    }

    const eventName = `company-${user.companyId}-whatsappSession`;

    const handleSessionUpdate = data => {
      const session = data?.session;

      if (
        data?.action === "update" &&
        session?.id === whatsapp.connectionId &&
        session?.status === "CONNECTED"
      ) {
        updateWhatsapp({
          connected: true,
          connectionId: session.id
        });

        setQrModalOpen(false);
      }
    };

    socket.on(eventName, handleSessionUpdate);

    return () => {
      socket.off(eventName, handleSessionUpdate);
    };
  }, [
    socket,
    user?.companyId,
    whatsapp?.connectionId,
    updateWhatsapp
  ]);

  return (
    <>
      <QrcodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        whatsAppId={whatsapp?.connectionId}
        companyId={user?.companyId}
      />

      <KoraxOnboardingShell
      step={3}
      totalSteps={7}
    >
      <KoraxOnboardingContent
        eyebrow="Canal de atendimento"
        title="Conecte seu WhatsApp"
        description="Vincule o número principal da sua operação para que a Korax possa organizar os atendimentos e preparar sua inteligência artificial."
      >
        <div className={classes.connectionCard}>
          <div className={classes.icon}>
            <WhatsAppIcon className={classes.iconSvg} />
          </div>

          <div className={classes.information}>
            <Typography className={classes.title}>
              WhatsApp da empresa
            </Typography>

            <Typography className={classes.text}>
              A conexão será usada para centralizar conversas, acompanhar clientes e ativar os recursos comerciais da Korax.
            </Typography>
          </div>

          <div className={classes.actionArea}>
            {!whatsapp?.connected ? (
              <Button
                className={classes.connectButton}
                variant="contained"
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Conectar WhatsApp"
                )}
              </Button>
            ) : (
              <div className={classes.status}>
                <CheckCircleRoundedIcon className={classes.statusIcon} />

                <div>
                  <Typography className={classes.statusTitle}>
                    Número conectado
                  </Typography>

                  <Typography className={classes.statusText}>
                    WhatsApp principal
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={classes.note}>
          <Typography className={classes.noteTitle}>
            Conexão segura e automática
          </Typography>

          <Typography className={classes.noteText}>
            Ao clicar em conectar, a Korax prepara automaticamente sua conexão e exibe o QR Code para vincular o número da empresa.
          </Typography>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        disableNext={!whatsapp?.connected}
      />
      </KoraxOnboardingShell>
    </>
  );
}
